import Phaser from 'phaser';

/**
 * Scene-provided baseline values that define the camera's spatial frame:
 *
 *   - {@link paddingLeft}: a constant world-space gap maintained between the
 *     viewport's left edge and the follow target. The camera enforces this
 *     every frame while followX is enabled, so the cursor stays anchored at
 *     the same world-space distance from the left edge regardless of zoom.
 *
 *   - {@link maxZoom}: the absolute camera zoom that corresponds to a
 *     "fully fit" view of the content. Inputs to {@link CameraController.zoomTo}
 *     are interpreted as a fraction of this value.
 *
 * Scenes provide these via {@link CameraController.setBaselines} (typically on
 * resize, once phrase layout is known).
 */
export interface ZoomBaselines {
  paddingLeft: number;
  maxZoom: number;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface CameraControllerConfig {
  /** Enable animated zoom via tweens. */
  zoomEnabled?: boolean;
  /** Enable horizontal follow of a target game object each frame. */
  followXEnabled?: boolean;
  /** Enable vertical follow of a target game object each frame. */
  followYEnabled?: boolean;
  /** Enable user-driven scroll input (wheel + pointer drag). */
  manualScrollEnabled?: boolean;
  /** Axis for manual scroll input: 'x' | 'y' | 'both'. Default 'y'. */
  manualScrollAxis?: 'x' | 'y' | 'both';
  /** Multiplier applied to wheel/drag deltas. Default 1.5. */
  scrollSpeed?: number;
  /** Fixed offset applied when following a target on X. */
  followOffsetX?: number;
  /** Fixed offset applied when following a target on Y. */
  followOffsetY?: number;
  /** Duration (ms) for smooth scroll tweens. Default 400. */
  scrollTweenDuration?: number;
  /** Easing function name for scroll tweens. Default 'Cubic.easeInOut'. */
  scrollTweenEase?: string;
  /** Duration (ms) for zoom tweens. Default 500. */
  zoomTweenDuration?: number;
  /** Easing function name for zoom tweens. Default 'Power2'. */
  zoomTweenEase?: string;
  /** Min relative zoom (clamps the input to {@link CameraController.zoomTo}). Default 0.5. */
  minRelativeZoom?: number;
  /** Max relative zoom (clamps the input to {@link CameraController.zoomTo}). Default 1.0. */
  maxRelativeZoom?: number;
  /** Max pointer movement (px) to count as a tap rather than a drag. Default 10. */
  tapThreshold?: number;
  /** Called when the user taps (pointer up with movement below tapThreshold). */
  onTap?: (pointer: Phaser.Input.Pointer) => void;
  /** If returns true, pointer drag scroll is suppressed (e.g. loop bracket being dragged). */
  shouldIgnoreDrag?: () => boolean;
  /** If returns false, wheel / drag scrolling does not run (e.g. only while playback is paused). */
  manualScrollAllowed?: () => boolean;
}

const DEFAULT_CONFIG: Required<CameraControllerConfig> = {
  zoomEnabled: false,
  followXEnabled: false,
  followYEnabled: false,
  manualScrollEnabled: false,
  manualScrollAxis: 'y',
  scrollSpeed: 1.5,
  followOffsetX: 0,
  followOffsetY: 0,
  scrollTweenDuration: 400,
  scrollTweenEase: 'Cubic.easeInOut',
  zoomTweenDuration: 500,
  zoomTweenEase: 'Power2',
  minRelativeZoom: 0.5,
  maxRelativeZoom: 1.0,
  tapThreshold: 10,
  onTap: undefined as unknown as (pointer: Phaser.Input.Pointer) => void,
  shouldIgnoreDrag: undefined as unknown as () => boolean,
  manualScrollAllowed: undefined as unknown as () => boolean,
};

// ---------------------------------------------------------------------------
// Follow target interface
// ---------------------------------------------------------------------------

/** Anything that exposes a world position can be a follow target. */
export interface FollowTarget {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

// ---------------------------------------------------------------------------
// CameraController
// ---------------------------------------------------------------------------

/**
 * Encapsulates camera scrolling, following, and zoom logic for Phaser scenes.
 *
 * Usage:
 *   const cam = new CameraController(scene, { followXEnabled: true, zoomEnabled: true });
 *   cam.setFollowTarget(cursor);
 *   // call cam.update() from scene.update()
 *   // call cam.destroy() from scene.destroy()
 */
export class CameraController {
  private scene: Phaser.Scene;
  private config: Required<CameraControllerConfig>;
  private camera: Phaser.Cameras.Scene2D.Camera;

  // -- Follow state --
  private followTarget: FollowTarget | null = null;

  // -- Zoom state --
  private zoomTween: Phaser.Tweens.Tween | null = null;
  /** Last requested relative zoom (input domain — fraction of {@link maxZoom}). */
  private currentRelativeZoom: number = 1;
  /** World-space gap between viewport-left's world coordinate and the follow
   *  target. Either set directly via {@link setBaselines} (desktop, value 0)
   *  or derived from a left-edge anchor via {@link setLeftAnchor} (mobile,
   *  recomputed each frame so the anchor's *screen-pixel* offset stays fixed
   *  across zoom changes). */
  private paddingLeft: number = 0;
  /** Anchor used to derive paddingLeft on mobile: the world-x of a fixed
   *  reference point (e.g. the first-phrase brace) plus the *screen-pixel*
   *  gap that should be held between viewport-left and that reference,
   *  regardless of camera zoom. */
  private leftAnchorWorldX?: number;
  private leftAnchorScreenGap: number = 0;
  /** Follow target's world-x at the moment the anchor was wired up. Preserves
   *  the initial (followTarget − anchor) offset so playback's 1:1 follow math
   *  carries over even though paddingLeft is recomputed each frame. */
  private leftAnchorCapturedFollowX: number = 0;
  /** Absolute camera zoom corresponding to "fully fit" content. */
  private maxZoom: number = 1;
  private pendingZoom: {
    level: number;
    options?: { onUpdate?: () => void; onComplete?: () => void };
  } | null = null;
  private baselinesSet: boolean = false;

  // -- Scroll tween state --
  private scrollTween: Phaser.Tweens.Tween | null = null;
  private isAutoScrolling: boolean = false;

  // -- Manual scroll input state --
  private isDragging: boolean = false;
  private dragStartPointerX: number = 0;
  private dragStartPointerY: number = 0;
  private dragStartScrollX: number = 0;
  private dragStartScrollY: number = 0;
  private boundWheelHandler?: (event: WheelEvent) => void;
  /** After user pans while paused, skip follow-X until {@link snapToTarget}. */
  private followXSuspendedAfterManualPan: boolean = false;

  // -- Callbacks --
  private onZoomUpdate?: () => void;
  private onZoomComplete?: () => void;

  constructor(scene: Phaser.Scene, config: CameraControllerConfig = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.camera = scene.cameras.main;

    if (this.config.manualScrollEnabled) {
      this.setupManualScrollInput();
    }

    // Follow on `prerender` (after scene.update + tween processing, before the
    // camera matrix is built for this frame) so scrollX always reflects the
    // follow target's *current* position. Running follow before the target
    // moves this frame would lag the camera by one frame and — when per-frame
    // movement varies (lerp easing, count-in, tempo changes) — show up as
    // visible shake.
    scene.events.on('prerender', this.update, this);
  }

  // ---------------------------------------------------------------------------
  // Public API — Configuration
  // ---------------------------------------------------------------------------

  /** Update config values at runtime (merges with existing). */
  setConfig(partial: Partial<CameraControllerConfig>): void {
    const wasManualScroll = this.config.manualScrollEnabled;
    Object.assign(this.config, partial);

    if (!wasManualScroll && this.config.manualScrollEnabled) {
      this.setupManualScrollInput();
    } else if (wasManualScroll && !this.config.manualScrollEnabled) {
      this.teardownManualScrollInput();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — Follow
  // ---------------------------------------------------------------------------

  /** Set the object the camera should follow each frame. Pass null to stop following. */
  setFollowTarget(target: FollowTarget | null): void {
    this.followTarget = target;
    if (this.leftAnchorWorldX !== undefined && target) {
      this.leftAnchorCapturedFollowX = target.x;
    }
    this.recomputePaddingLeftFromAnchor();
  }

  /**
   * Pin the viewport's left edge so that `worldX` sits at `screenGap` *screen*
   * pixels to the right of it, regardless of camera zoom. The follow target's
   * position at call time is captured to preserve the initial (cursor − anchor)
   * world offset, so during playback `scrollX` still tracks the cursor 1:1 with
   * that initial offset. `paddingLeft` is recomputed each frame in
   * {@link update} so a zoom tween doesn't let the anchor drift on screen.
   *
   * Caller responsibility: pass the anchor's *world* position. If the anchor
   * is a child of a container, multiply / transform appropriately before
   * passing the value in.
   */
  setLeftAnchor(worldX: number, screenGap: number = 0): void {
    this.leftAnchorWorldX = worldX;
    this.leftAnchorScreenGap = screenGap;
    if (this.followTarget) {
      this.leftAnchorCapturedFollowX = this.followTarget.x;
    }
    this.recomputePaddingLeftFromAnchor();
  }

  /** Whether a left anchor has been wired up. Lets callers detect a one-time
   *  setup so they only configure the anchor once. */
  hasLeftAnchor(): boolean {
    return this.leftAnchorWorldX !== undefined;
  }

  /** Drop the left anchor — paddingLeft stays at whatever it last was, but
   *  subsequent setFollowTarget calls won't re-derive it. */
  clearLeftAnchor(): void {
    this.leftAnchorWorldX = undefined;
  }

  /** The current world-space gap between the follow target and viewport-left.
   *  Useful for callers predicting a future scrollX (e.g. computing the
   *  on-screen position a follow target will occupy *after* a move). */
  getPaddingLeft(): number {
    return this.paddingLeft;
  }

  private recomputePaddingLeftFromAnchor(): void {
    if (this.leftAnchorWorldX === undefined) return;
    // Hold the anchor at `leftAnchorScreenGap` *screen* pixels from the
    // viewport's left edge regardless of zoom. Phaser's
    //   worldView.x = scrollX + halfWidth * (1 - 1/zoom)
    // and we want
    //   (anchor.worldX - worldView.x) * zoom = screenGap
    // Solving for paddingLeft (= followTarget.x - scrollX) with the captured
    // follow-x preserves playback's 1:1 follow from the initial offset:
    //   paddingLeft = (capturedFollowX - anchor.worldX) + halfWidth + (screenGap - halfWidth)/zoom
    const halfWidth = this.camera.width / 2;
    const zoom = this.camera.zoom;
    this.paddingLeft =
      this.leftAnchorCapturedFollowX -
      this.leftAnchorWorldX +
      halfWidth +
      (this.leftAnchorScreenGap - halfWidth) / zoom;
  }

  getFollowTarget(): FollowTarget | null {
    return this.followTarget;
  }

  // ---------------------------------------------------------------------------
  // Public API — Zoom
  // ---------------------------------------------------------------------------

  /**
   * Set the scene-provided zoom + spatial baselines.
   *
   * Typically called from the scene's resize flow once phrase layout is known.
   *
   *   - `paddingLeft`: world-space gap maintained between viewport left and the
   *     follow target (applied each frame in {@link update} when followX is on).
   *   - `maxZoom`: absolute camera zoom corresponding to a fully-fit view; all
   *     {@link zoomTo} inputs are interpreted as a fraction of this value.
   *
   * If a {@link zoomTo} call was queued before the first baselines arrived, it
   * is applied now. On subsequent calls (e.g. on resize), the current relative
   * zoom is re-applied so the camera reflects the new {@link maxZoom}.
   */
  setBaselines({ paddingLeft, maxZoom }: ZoomBaselines): void {
    this.paddingLeft = paddingLeft;
    this.maxZoom = maxZoom;
    const wasSet = this.baselinesSet;
    this.baselinesSet = true;

    if (this.pendingZoom) {
      const { level, options } = this.pendingZoom;
      this.pendingZoom = null;
      this.zoomTo(level, options);
    } else if (wasSet) {
      this.zoomTo(this.currentRelativeZoom);
    }
  }

  /**
   * Animate the camera to a target zoom level, expressed as a fraction of the
   * scene-provided {@link ZoomBaselines.maxZoom} (e.g. `0.9` = 90% of fit).
   *
   * The relative input is clamped to [{@link CameraControllerConfig.minRelativeZoom},
   * {@link CameraControllerConfig.maxRelativeZoom}] and the resulting absolute
   * camera zoom is `clamped * maxZoom`.
   *
   * If baselines haven't been provided yet (via {@link setBaselines}), the call
   * is queued and applied automatically once they arrive.
   */
  zoomTo(
    relativeLevel: number,
    options?: { onUpdate?: () => void; onComplete?: () => void },
  ): void {
    if (!this.config.zoomEnabled) return;

    if (!this.baselinesSet) {
      this.pendingZoom = { level: relativeLevel, options };
      return;
    }

    const clamped = Phaser.Math.Clamp(
      relativeLevel,
      this.config.minRelativeZoom,
      this.config.maxRelativeZoom,
    );
    this.currentRelativeZoom = clamped;
    const targetZoom = clamped * this.maxZoom;

    this.zoomTween?.stop();
    this.onZoomUpdate = options?.onUpdate;
    this.onZoomComplete = options?.onComplete;

    this.zoomTween = this.scene.tweens.add({
      targets: this.camera,
      zoom: targetZoom,
      duration: this.config.zoomTweenDuration,
      ease: this.config.zoomTweenEase,
      onUpdate: () => {
        this.onZoomUpdate?.();
      },
      onComplete: () => {
        this.onZoomComplete?.();
        this.onZoomUpdate = undefined;
        this.onZoomComplete = undefined;
      },
    });
  }

  /** Get the current relative zoom (the input domain — fraction of maxZoom). */
  getRelativeZoom(): number {
    return this.currentRelativeZoom;
  }

  /** Get the actual camera zoom value. */
  getCameraZoom(): number {
    return this.camera.zoom;
  }

  /**
   * Update only the paddingLeft used by the follow logic, without touching
   * maxZoom or restarting the zoom tween. Use this when the paddingLeft is a
   * function of `camera.zoom` (e.g. a fixed screen-pixel gap whose world
   * equivalent is `screenPx / zoom`) and needs to be recomputed each frame.
   */
  setPaddingLeft(value: number): void {
    this.paddingLeft = value;
  }

  // ---------------------------------------------------------------------------
  // Public API — Scroll
  // ---------------------------------------------------------------------------

  /** Smoothly scroll the camera to a target scrollY position. */
  scrollToY(targetY: number, options?: { duration?: number; onComplete?: () => void }): void {
    const bounds = this.camera.getBounds();
    const maxScrollY = Math.max(0, bounds.height - this.camera.height);
    const clampedY = Phaser.Math.Clamp(targetY, 0, maxScrollY);

    this.isAutoScrolling = true;
    this.scrollTween?.stop();
    this.scrollTween = this.scene.tweens.add({
      targets: this.camera,
      scrollY: clampedY,
      duration: options?.duration ?? this.config.scrollTweenDuration,
      ease: this.config.scrollTweenEase,
      onComplete: () => {
        this.isAutoScrolling = false;
        options?.onComplete?.();
      },
    });
  }

  /** Smoothly scroll the camera to a target scrollX position. */
  scrollToX(targetX: number, options?: { duration?: number; onComplete?: () => void }): void {
    const bounds = this.camera.getBounds();
    const maxScrollX = Math.max(0, bounds.width - this.camera.width);
    const clampedX = Phaser.Math.Clamp(targetX, 0, maxScrollX);

    this.isAutoScrolling = true;
    this.scrollTween?.stop();
    this.scrollTween = this.scene.tweens.add({
      targets: this.camera,
      scrollX: clampedX,
      duration: options?.duration ?? this.config.scrollTweenDuration,
      ease: this.config.scrollTweenEase,
      onComplete: () => {
        this.isAutoScrolling = false;
        options?.onComplete?.();
      },
    });
  }

  /** Immediately set scrollX (no animation). */
  setScrollX(value: number): void {
    this.camera.scrollX = value;
  }

  /** Immediately set scrollY (no animation). */
  setScrollY(value: number): void {
    this.camera.scrollY = value;
  }

  getScrollX(): number {
    return this.camera.scrollX;
  }

  getScrollY(): number {
    return this.camera.scrollY;
  }

  /** Whether a programmatic scroll tween is currently in flight. */
  getIsAutoScrolling(): boolean {
    return this.isAutoScrolling;
  }

  // ---------------------------------------------------------------------------
  // Public API — Bounds
  // ---------------------------------------------------------------------------

  /** Set the camera world bounds. */
  setBounds(x: number, y: number, width: number, height: number): void {
    this.camera.setBounds(x, y, width, height);
  }

  // ---------------------------------------------------------------------------
  // Public API — Snap
  // ---------------------------------------------------------------------------

  /**
   * Immediately align the camera to the current follow target's position.
   * Use this when the target jumps abruptly (e.g. cursor repositioned on pause,
   * seek, or measure rollback) so the camera doesn't lag behind or stay stale.
   *
   * Cancels any in-flight scroll tween to avoid fighting.
   */
  snapToTarget(): void {
    if (!this.followTarget) return;

    this.followXSuspendedAfterManualPan = false;

    this.scrollTween?.stop();
    this.isAutoScrolling = false;

    if (this.config.followXEnabled) {
      this.camera.scrollX = this.followTarget.x + this.config.followOffsetX - this.paddingLeft;
    }

    if (this.config.followYEnabled) {
      this.camera.scrollY = this.followTarget.y + this.config.followOffsetY;
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — Update (call from scene.update)
  // ---------------------------------------------------------------------------

  /**
   * Must be called every frame from the scene's update() method.
   * Handles follow-target tracking.
   */
  update(): void {
    if (!this.followTarget) return;

    // Re-derive paddingLeft each frame against the live camera.zoom so the
    // anchor's screen-pixel offset stays fixed across zoom tweens. No-op if
    // no left anchor was ever wired up (e.g. desktop).
    this.recomputePaddingLeftFromAnchor();

    const axis = this.config.manualScrollAxis;
    const draggingManualX =
      this.isDragging && this.config.manualScrollEnabled && (axis === 'x' || axis === 'both');
    const draggingManualY =
      this.isDragging && this.config.manualScrollEnabled && (axis === 'y' || axis === 'both');

    if (
      this.config.followXEnabled &&
      !this.isAutoScrolling &&
      !draggingManualX &&
      !this.followXSuspendedAfterManualPan
    ) {
      const targetX = this.followTarget.x + this.config.followOffsetX - this.paddingLeft;
      // NaN/Infinity in scrollX kills the entire render pipeline (the whole
      // canvas goes blank). Skip the assignment and surface the bad inputs so
      // we can trace the upstream cause (most likely Math.min over an empty/
      // undefined positions list in the cursor iterator).
      if (Number.isFinite(targetX)) {
        this.camera.scrollX = targetX;
      } else {
        console.warn('[CameraController] non-finite followX target, skipping', {
          targetX,
          followX: this.followTarget.x,
          followOffsetX: this.config.followOffsetX,
          paddingLeft: this.paddingLeft,
        });
      }
    }

    if (this.config.followYEnabled && !this.isAutoScrolling && !draggingManualY) {
      const targetY = this.followTarget.y + this.config.followOffsetY;
      if (Number.isFinite(targetY)) {
        this.camera.scrollY = targetY;
      } else {
        console.warn('[CameraController] non-finite followY target, skipping', {
          targetY,
          followY: this.followTarget.y,
          followOffsetY: this.config.followOffsetY,
        });
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — Lifecycle
  // ---------------------------------------------------------------------------

  destroy(): void {
    this.scene.events.off('prerender', this.update, this);
    this.teardownManualScrollInput();
    this.zoomTween?.stop();
    this.scrollTween?.stop();
    this.followTarget = null;
    this.followXSuspendedAfterManualPan = false;
    this.onZoomUpdate = undefined;
    this.onZoomComplete = undefined;
  }

  private isManualScrollAllowed(): boolean {
    return this.config.manualScrollAllowed?.() ?? true;
  }

  // ---------------------------------------------------------------------------
  // Internals — Manual scroll input
  // ---------------------------------------------------------------------------

  private setupManualScrollInput(): void {
    const scene = this.scene;

    scene.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        deltaX: number,
        deltaY: number,
      ) => {
        if (!this.isManualScrollAllowed()) return;

        const bounds = this.camera.getBounds();
        const axis = this.config.manualScrollAxis;

        if (axis === 'y' || axis === 'both') {
          const maxScrollY = Math.max(0, bounds.height - this.camera.height);
          const delta = deltaY * this.config.scrollSpeed;
          this.camera.scrollY = Phaser.Math.Clamp(this.camera.scrollY + delta, 0, maxScrollY);
        }

        if (axis === 'x' || axis === 'both') {
          const maxScrollX = Math.max(0, bounds.width - this.camera.width);
          const delta = deltaX * this.config.scrollSpeed;
          this.camera.scrollX = Phaser.Math.Clamp(this.camera.scrollX + delta, 0, maxScrollX);
        }
      },
    );

    const canvas = scene.game.canvas;
    this.boundWheelHandler = (event: WheelEvent) => {
      event.preventDefault();
    };
    canvas.addEventListener('wheel', this.boundWheelHandler, { passive: false });

    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.config.shouldIgnoreDrag?.()) return;
      if (!this.isManualScrollAllowed()) return;
      this.isDragging = true;
      this.dragStartPointerX = pointer.x;
      this.dragStartPointerY = pointer.y;
      this.dragStartScrollX = this.camera.scrollX;
      this.dragStartScrollY = this.camera.scrollY;
    });

    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      if (!this.isManualScrollAllowed()) {
        this.isDragging = false;
        return;
      }
      if (this.config.shouldIgnoreDrag?.()) {
        this.isDragging = false;
        return;
      }

      const bounds = this.camera.getBounds();
      const axis = this.config.manualScrollAxis;

      if (axis === 'y' || axis === 'both') {
        const dragDeltaY = this.dragStartPointerY - pointer.y;
        const maxScrollY = Math.max(0, bounds.height - this.camera.height);
        this.camera.scrollY = Phaser.Math.Clamp(this.dragStartScrollY + dragDeltaY, 0, maxScrollY);
      }

      if (axis === 'x' || axis === 'both') {
        const dragDeltaX = this.dragStartPointerX - pointer.x;
        const maxScrollX = Math.max(0, bounds.width - this.camera.width);
        this.camera.scrollX = Phaser.Math.Clamp(this.dragStartScrollX + dragDeltaX, 0, maxScrollX);
      }
    });

    scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        const dx = pointer.x - this.dragStartPointerX;
        const dy = pointer.y - this.dragStartPointerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < this.config.tapThreshold) {
          this.config.onTap?.(pointer);
        } else if (
          this.isManualScrollAllowed() &&
          this.config.followXEnabled &&
          (this.config.manualScrollAxis === 'x' || this.config.manualScrollAxis === 'both') &&
          Math.abs(this.camera.scrollX - this.dragStartScrollX) > 0.5
        ) {
          this.followXSuspendedAfterManualPan = true;
        }
      }
      this.isDragging = false;
    });

    scene.input.on('pointerupoutside', () => {
      this.isDragging = false;
    });
  }

  private teardownManualScrollInput(): void {
    if (this.boundWheelHandler) {
      this.scene.game.canvas.removeEventListener('wheel', this.boundWheelHandler);
      this.boundWheelHandler = undefined;
    }
    this.isDragging = false;
  }
}
